import React, { useState } from "react";
import { ArrowUpRight, Plus, Minus, ShieldCheck, Activity, Heart } from "lucide-react";
import { INTAKE_URL } from "@/constants/links";

export function ManifestoBand({ manifesto }) {
  if (!manifesto) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-white py-16 sm:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <div className="iw-overline mb-3">§ Manifesto</div>
          <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl">
            <span className="text-[color:var(--iw-accent-red)]">{manifesto.headline}</span>
          </h3>
        </div>
        <div className="lg:col-span-7">
          <p className="text-lg leading-relaxed text-[color:var(--iw-body)] sm:text-xl">
            {manifesto.body}
          </p>
        </div>
      </div>
    </section>
  );
}

export function TechCardsBand({ techCards }) {
  if (!techCards?.length) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="iw-overline mb-3">§ The Tech</div>
          <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl">
            What's behind your <span className="text-[color:var(--iw-blue)]">plan</span>.
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-border)] sm:grid-cols-2 lg:grid-cols-4">
          {techCards.map((card, i) => (
            <div
              key={card.title}
              className="group relative bg-white p-6 transition-colors hover:bg-[color:var(--iw-bg)] sm:p-7"
            >
              <div className="absolute left-0 top-0 h-1 w-0 bg-[color:var(--iw-accent-red)] transition-all duration-300 group-hover:w-full" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h4 className="mt-3 font-display text-lg font-semibold leading-tight tracking-tight text-[color:var(--iw-primary)]">
                {card.title}
              </h4>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--iw-body)]">
                {card.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MoatBand() {
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-primary)] py-16 text-white sm:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-9">
          <h3 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
            It's a remote optimization clinic — <span className="text-[color:var(--iw-accent-red)]">running quietly inside your coaching program.</span>
          </h3>
        </div>
        <div className="flex items-end lg:col-span-3 lg:justify-end">
          <span className="border border-white/20 bg-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white backdrop-blur">
            // measure → optimize → adapt → repeat
          </span>
        </div>
      </div>
    </section>
  );
}

const WHO_FOR_ICONS = [Heart, Activity, ShieldCheck];

export function WhoForBand({ whoForCards }) {
  if (!whoForCards?.length) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="iw-overline mb-3">§ Who it's for</div>
            <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl">
              Built for three kinds of people.
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-border)] md:grid-cols-3">
          {whoForCards.map((card, i) => {
            const Icon = WHO_FOR_ICONS[i] || ShieldCheck;
            return (
              <div key={card.title} className="bg-white p-7">
                <span className="inline-grid h-11 w-11 place-items-center border border-[color:var(--iw-blue)]/60 text-[color:var(--iw-blue)]">
                  <Icon className="h-5 w-5" />
                </span>
                <h4 className="mt-5 font-display text-xl font-semibold tracking-tight text-[color:var(--iw-primary)]">
                  {card.title}
                </h4>
                <p className="mt-3 text-base leading-relaxed text-[color:var(--iw-body)]">
                  {card.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PlaceholderResults() {
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10">
          <div className="iw-overline mb-3">§ Results</div>
          <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl">
            Member stories — <span className="text-[color:var(--iw-accent-red)]">coming soon.</span>
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="relative flex flex-col gap-5 border-2 border-dashed border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] p-7"
            >
              <span className="absolute right-3 top-3 border border-[color:var(--iw-border)] bg-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.22em] text-[color:var(--iw-muted-foreground)]">
                Placeholder
              </span>
              <div className="h-12 w-12 rounded-full bg-[color:var(--iw-border)]" />
              <p className="text-base leading-relaxed text-[color:var(--iw-body)]">
                "Member testimonial slot — real story will go here once we publish."
              </p>
              <div className="mt-auto font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-muted-foreground)]">
                Member · GI.03
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PLACEHOLDER_PLANS = [
  { name: "Starter", tag: "Try it", price: "$—", lines: ["Weekly check-in", "AI nutrition plan", "Coach review"] },
  { name: "Core", tag: "Most popular", price: "$—", lines: ["Everything in Starter", "Adaptive habit plan", "Trend dashboards"], featured: true },
  { name: "Plus", tag: "Hands on", price: "$—", lines: ["Everything in Core", "1:1 coaching calls", "Optional medical referral"] },
];

export function PlaceholderPlans() {
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="iw-overline mb-3">§ Plans</div>
            <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl">
              Three ways to start.
            </h3>
          </div>
          <span className="hidden border border-[color:var(--iw-border)] bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-muted-foreground)] sm:inline-block">
            Pricing — Placeholder
          </span>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {PLACEHOLDER_PLANS.map((p) => (
            <div
              key={p.name}
              className={`flex flex-col gap-5 border bg-white p-7 ${
                p.featured ? "border-[color:var(--iw-accent-red)] shadow-[0_18px_42px_-18px_rgba(200,16,46,0.4)]" : "border-[color:var(--iw-border)]"
              }`}
            >
              <div className="flex items-center justify-between">
                <h4 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--iw-primary)]">
                  {p.name}
                </h4>
                <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${p.featured ? "text-[color:var(--iw-accent-red)]" : "text-[color:var(--iw-muted-foreground)]"}`}>
                  {p.tag}
                </span>
              </div>
              <div className="font-display text-4xl font-semibold tracking-tight text-[color:var(--iw-primary)]">
                {p.price}
                <span className="ml-1 font-mono text-xs uppercase tracking-[0.22em] text-[color:var(--iw-muted-foreground)]">
                  / mo
                </span>
              </div>
              <ul className="space-y-2 text-sm leading-relaxed text-[color:var(--iw-body)]">
                {p.lines.map((l) => (
                  <li key={l} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[color:var(--iw-accent-red)]" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
              <a
                href={INTAKE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] ${
                  p.featured ? "iw-btn-red" : "iw-btn-ghost"
                }`}
              >
                Get Started <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrackFaq({ faq, referralNote }) {
  const [open, setOpen] = useState(null);
  if (!faq?.length) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="mb-10">
          <div className="iw-overline mb-3">§ FAQ</div>
          <h3 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl">
            Common questions.
          </h3>
        </div>
        <div className="border-t border-[color:var(--iw-border)]">
          {faq.map((item, idx) => {
            const isOpen = open === idx;
            return (
              <div key={item.q} className="border-b border-[color:var(--iw-border)]">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : idx)}
                  className="flex w-full items-start justify-between gap-6 py-5 text-left transition-colors hover:bg-[color:var(--iw-bg)]"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-lg font-semibold leading-tight tracking-tight text-[color:var(--iw-primary)] sm:text-xl">
                      {item.q}
                    </span>
                  </span>
                  <span className="inline-grid h-7 w-7 flex-shrink-0 place-items-center border border-[color:var(--iw-border)] text-[color:var(--iw-primary)]">
                    {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="pb-6 pl-12 pr-2">
                    <p className="text-base leading-relaxed text-[color:var(--iw-body)]">{item.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {referralNote && (
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--iw-muted-foreground)]">
            {referralNote}
          </p>
        )}
      </div>
    </section>
  );
}

export function FinalCtaBand({ finalCta }) {
  if (!finalCta) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-primary)] py-16 text-white sm:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-12">
        <div className="lg:col-span-8">
          <h3 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            {finalCta.headline}
          </h3>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:col-span-4 lg:justify-end">
          <a
            href={finalCta.primary.href}
            className="iw-btn-red inline-flex items-center justify-center gap-2 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em]"
          >
            {finalCta.primary.label} <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href={finalCta.secondary.href}
            className="inline-flex items-center justify-center gap-2 border border-white/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white hover:bg-white/10"
          >
            {finalCta.secondary.label}
          </a>
        </div>
      </div>
    </section>
  );
}

export function ComplianceDisclaimer() {
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] py-10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-muted-foreground)]">
          Wellness program · Not a substitute for medical care · Not for diagnosis or treatment of any condition · If clinical support is needed, we refer you to the affiliated medical team.
        </p>
      </div>
    </section>
  );
}

export function ScanReadoutOverlay({ readouts }) {
  if (!readouts?.length) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex flex-wrap items-end justify-center gap-2 px-4">
      {readouts.map((r) => (
        <span
          key={r.label}
          className="border border-[color:var(--iw-blue-bright)]/60 bg-[color:var(--iw-primary)]/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white backdrop-blur"
        >
          <span className="text-[color:var(--iw-accent-red)]">●</span> {r.label} <span className="text-[color:var(--iw-blue-bright)]">{r.value}</span>
        </span>
      ))}
    </div>
  );
}
